import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LoanNotesDataStore } from '../../../loan-state/loan-notes/loan-notes.store';
import { LoanNotesQuery } from '../../../loan-state/loan-notes/loan-notes.query';
import { debounceTime, Subject, take, takeUntil, tap } from 'rxjs';
import { Models } from '../../../models';
import { Cmg1003Service } from '../../../cmg-1003/services/cmg-1003.service';
import { LeadNotesDataStore } from '../../state/lead-notes.store';
import { LeadNotesQuery } from '../../state/lead-notes.query';
import { ApplicationNotesDataStore } from '../../state/application-notes.store';
import { ApplicationNotesQuery } from '../../state/application-notes.query';
import { LoanApplicationNote } from '../../../cmg-1003/models/notes.model';
import { PartnerNotesDataStore } from '../../state/partner-notes.store';
import { PartnerNotesQuery } from '../../state/partner-notes.query';
import { Textarea } from 'primeng/textarea';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'cmg-clear2-ui-notes-panel',
  templateUrl: './notes-panel.component.html',
  styleUrls: ['./notes-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, Textarea, DecimalPipe],
})
export class NotesPanelComponent implements OnInit, OnDestroy {
  private readonly _applicationNotesQuery = inject(ApplicationNotesQuery);
  private readonly _applicationNotesStore = inject(ApplicationNotesDataStore);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _cmg1003Service = inject(Cmg1003Service);
  private readonly _leadNotesQuery = inject(LeadNotesQuery);
  private readonly _leadNotesStore = inject(LeadNotesDataStore);
  private readonly _loanNotesQuery = inject(LoanNotesQuery);
  private readonly _loanNotesStore = inject(LoanNotesDataStore);
  private readonly _partnerNotesQuery = inject(PartnerNotesQuery);
  private readonly _partnerNotesStore = inject(PartnerNotesDataStore);

  maxNoteLength: number = 25000;

  private _loanStatusId: number;
  @Input() set loanStatusId(loanStatusId: number) {
    if (this._loanStatusId !== loanStatusId) {
      this._loanStatusId = loanStatusId;
    }
  }

  readonly pipelineType = input<Models.PipelineType>(undefined);

  private _destroyed$ = new Subject<boolean>();

  private _fileDataId: number = null;
  @Input() set fileDataId(fileDataId: number) {
    if (this._fileDataId !== fileDataId) {
      this._fileDataId = fileDataId;
      this._initLoanNotes();
    }
  }

  private _leadGUID: string = null;
  @Input() set leadGUID(leadGUID: string) {
    if (this._leadGUID !== leadGUID) {
      this._leadGUID = leadGUID;
      this._initLeadNotes();
    }
  }

  private _partnerGuid: string = null;
  @Input() set partnerGuid(partnerGuid: string) {
    if (this._partnerGuid !== partnerGuid) {
      this._partnerGuid = partnerGuid;
      this._initPartnerNotes();
    }
  }

  @Input() set isConverted(isConverted: boolean) {
    isConverted ? this.notesControl.disable() : this.notesControl.enable;
  }

  notesControl: FormControl<string> = new FormControl('');

  ngOnInit(): void {
    this.notesControl.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        debounceTime(400),
        tap((noteContent: string) => {
          switch (this.pipelineType()) {
            case Models.PipelineType.AllLoans:
            case Models.PipelineType.Loan:
              this._updateLoanNotes(noteContent);
              break;
            case Models.PipelineType.LoanApplication:
              this._updateApplicationLoanNotes(noteContent);
              break;
            case Models.PipelineType.Lead:
              this._updateLeadNotesStore(noteContent);
              break;
            case Models.PipelineType.Partners:
              this._updatePartnerNotesStore(noteContent);
              break;
          }

          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  private _initLoanNotes(): void {
    this._loanNotesQuery
      .select()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((data) => {
        if (data.content !== this.notesControl.value) {
          // { emitEvent: false }, so that store isn't prematurely marked dirty (via valueChanges).
          this.notesControl.patchValue(this._loanNotesQuery.getValue().content, { emitEvent: false });

          this._changeDetectorRef.detectChanges();
        }
      });
  }

  private _loanApplicationGUID: string = null;
  @Input() set applicationGUID(applicationGUID: string) {
    if (this._loanApplicationGUID !== applicationGUID) {
      this._loanApplicationGUID = applicationGUID;
      this._initApplicationLoanNotes();
    }
  }

  private _initApplicationLoanNotes(): void {
    this._cmg1003Service
      .getLoanApplicationNotes(this._loanApplicationGUID)
      .pipe(
        take(1),
        tap((applicationNotes) => {
          // Start with a blank slate in application Notes store.
          this._applicationNotesStore.reset();

          // applicationGUID has a fallback because applicationNotes may be entirely null.
          // This can happen since a applicationNotes can exist without any notes - often seen for applicationNotes created with v.1 form.
          const applicationNote: LoanApplicationNote =
            applicationNotes?.length > 1
              ? applicationNotes.reduce((latest, current) => {
                  return new Date(current.dateUpdated) > new Date(latest.dateUpdated) ? current : latest;
                }, applicationNotes[0])
              : applicationNotes[0];
          this._applicationNotesStore.update({
            ...applicationNote,
            loanApplicationGUID: applicationNote?.loanApplicationGUID || this._loanApplicationGUID,
          });
          this._applicationNotesQuery.dirtyChecker2.dirtyChecker.setHead();

          // { emitEvent: false }, so that store isn't prematurely marked dirty (via valueChanges).
          this.notesControl.patchValue(applicationNote?.content, { emitEvent: false });

          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();
  }

  private _updateLoanNotes(value: string): void {
    this._loanNotesStore.update({
      content: value,
      byteFileDataID: this._fileDataId,
    });
  }

  private _updateApplicationLoanNotes(updatedContent: string): void {
    this._applicationNotesStore.update({
      content: updatedContent,
    });
  }

  private _initLeadNotes(): void {
    this._cmg1003Service
      .getLeadNotes(this._leadGUID)
      .pipe(
        take(1),
        tap((leadNotes) => {
          // Start with a blank slate in Leads Notes store.
          this._leadNotesStore.reset();

          // leadGUID has a fallback because leadNotes may be entirely null.
          // This can happen since a lead can exist without any notes - often seen for leads created with v.1 form.
          this._leadNotesStore.update({ ...leadNotes, leadGUID: leadNotes?.leadGUID || this._leadGUID });
          this._leadNotesQuery.dirtyChecker.setHead();

          // { emitEvent: false }, so that store isn't prematurely marked dirty (via valueChanges).
          this.notesControl.patchValue(leadNotes?.content, { emitEvent: false });

          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();
  }

  private _updateLeadNotesStore(updatedContent: string): void {
    this._leadNotesStore.update({
      content: updatedContent,
    });
  }

  private _initPartnerNotes(): void {
    this.maxNoteLength = 5000;

    this._partnerNotesQuery
      .select()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((data) => {
        if (data.note !== this.notesControl.value) {
          this.notesControl.patchValue(this._partnerNotesQuery.getValue().note, { emitEvent: false });
          this._changeDetectorRef.detectChanges();
        }
      });
  }

  private _updatePartnerNotesStore(updatedContent: string): void {
    this._partnerNotesStore.update({
      note: updatedContent,
    });
  }
}
